// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import clsx from 'clsx'
import styles from './note-card.module.css'
import type { OwnerAnswers, OwnerQuestionSetDef, OwnerReadValue } from 'lib/dealhunt/types'
import { capitalize, lowerFirst } from 'lib/owner-notes/band-copy'
import { READ_WORD, readVerdict } from 'lib/owner-notes/card-owners'

export function answerLabel(
  options: Array<{ id: string; label: string }> | undefined,
  id: string
): string {
  return options?.find(o => o.id === id)?.label ?? id
}

export function formatNoteDate(iso: string | null | undefined): string {
  if (!iso) {
    return ''
  }
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

/**
 * "Owned under a month · Still using it: sometimes · Posted Sep 2026" — each answer carries
 * the question it answers, so a first-time reader knows what "Sometimes" is about.
 *
 * The posted date is the only machine-readable thing on a note, so it goes out as a `<time>`
 * carrying the payload's ISO `published_at` (SEO audit 2026-09-18) — same words, same row,
 * and nothing at all when the date is missing or unparseable.
 */
export function ownershipLine(
  answers: OwnerAnswers,
  questions: OwnerQuestionSetDef | null,
  date: string | null | undefined
): JSX.Element {
  const owned = answerLabel(questions?.ownership_length, answers.ownership_length)
  const using = answerLabel(questions?.still_using, answers.still_using)
  const posted = formatNoteDate(date)
  const said = [
    owned && `Owned ${lowerFirst(owned)}`,
    using && `Still using it: ${lowerFirst(using)}`,
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <>
      {said}
      {posted && (
        <>
          {said && ' · '}
          {'Posted '}
          <time dateTime={date ?? undefined}>{posted}</time>
        </>
      )}
    </>
  )
}

interface NoteAnswersProps {
  answers: OwnerAnswers
  questions: OwnerQuestionSetDef | null
}

/**
 * The public card's chips (design 2a, 2026-09-22): DEPARTURES ONLY — "Battery · better than
 * expected", "Mic on calls · worse than expected", plus the wear-out pick as "Ear pads · gave
 * out" (phrases, not key: value pairs — polish 2026-09-22). "As expected" answers
 * stay in the rail card's reads (`OwnerRailAsk`), not on the note; buy-again is the card's own mark,
 * not a chip. The read's label is shortened to what the design shows for the default set and
 * falls back to the set's label, lower-cased, for any other read.
 */
const SHORT_READ: Record<string, string> = {
  comfort: 'comfort',
  battery: 'battery',
  mic: 'mic on calls',
  build: 'fit and build',
}

export function shortReadLabel(read: { id: string; label: string }): string {
  return SHORT_READ[read.id] ?? lowerFirst(read.label)
}

export const DepartureChips: React.VFC<NoteAnswersProps> = ({ answers, questions }) => {
  const reads = questions?.reads ?? []
  const departures = reads.filter(read => {
    const value = answers.reads[read.id]
    return value === 'better' || value === 'worse'
  })
  const gaveOut =
    answers.gave_out && answers.gave_out !== 'nothing'
      ? answerLabel(questions?.gave_out, answers.gave_out)
      : null
  if (departures.length === 0 && !gaveOut) {
    return null
  }
  return (
    <ul className={styles.reads} aria-label="Where it departed from expectations">
      {departures.map(read => {
        const value = answers.reads[read.id] as Exclude<OwnerReadValue, 'expected'>
        return (
          <li key={read.id} className={clsx(styles.read, styles[`read_${value}`])}>
            {`${capitalize(shortReadLabel(read))} · ${readVerdict(value)}`}
          </li>
        )
      })}
      {gaveOut && (
        <li className={clsx(styles.read, styles.read_worse)}>{`${gaveOut} · gave out`}</li>
      )}
    </ul>
  )
}

/**
 * The reads, buy-again and hours as chips — the author's own card (`OwnNoteCard`); the public
 * card shows `DepartureChips` instead.
 *
 * Only DEPARTURES get a chip of their own (#119). A row of five grey "as expected" labels was
 * the loudest thing on a note while saying the least, and once the wall can be ordered by
 * experience the colour of these chips is how a reader CHECKS that the order did what it said —
 * which only works if the departures are what stands out. The count is kept, not dropped:
 * "2 as expected" is still the sample the verdicts sit in.
 */
const NoteAnswers: React.VFC<NoteAnswersProps> = ({ answers, questions }) => {
  const reads = questions?.reads ?? []
  const answered = reads.filter(read => answers.reads[read.id])
  const expected = answered.filter(read => answers.reads[read.id] === 'expected')
  return (
    <ul className={styles.reads} aria-label="Quick reads">
      {answered.map(read => {
        const value = answers.reads[read.id]
        if (!value || value === 'expected') {
          return null
        }
        return (
          <li key={read.id} className={clsx(styles.read, styles[`read_${value}`])}>
            <span className={styles.readLabel}>{read.label}</span>
            <span>{READ_WORD[value]}</span>
          </li>
        )
      })}
      {expected.length > 0 && (
        <li
          className={clsx(styles.read, styles.readRest)}
          title={expected.map(r => r.label).join(', ')}
        >
          <span>{`${expected.length} as expected`}</span>
        </li>
      )}
      {typeof answers.buy_again === 'boolean' && (
        <li
          className={clsx(styles.read, answers.buy_again ? styles.read_better : styles.read_worse)}
        >
          <span className={styles.readLabel}>{'Buy again'}</span>
          <span>{answers.buy_again ? 'yes' : 'no'}</span>
        </li>
      )}
      {typeof answers.hours_per_charge === 'number' && (
        <li className={clsx(styles.read, styles.read_expected)}>
          <span className={styles.readLabel}>{'Per charge'}</span>
          <span>{`${answers.hours_per_charge} h`}</span>
        </li>
      )}
    </ul>
  )
}

export default NoteAnswers
